import { useState } from 'react'
import ErrorMessage from '../ErrorMessage'
import Form from '../Form'
import ReferralInputs from '../ReferralInputs'
import { submitSignupPixels } from '@helpers/pixels'
import Section from '../Section'
import Input, { Button, Phone, Fieldset, isValidPhone } from '../Input'
import { InputOption } from '@favordelivery/tupperware'
import { Step2Container } from '@src/shared/styles/home'
import Footer from '../Footer'
import styled from 'styled-components'
import { useMountEffect } from '@src/shared/utils/hooks'
import { useAppDispatch } from '@src/client/store'
import { clearApplicantError } from '@store/applicant/actions'
import { t, tStr } from '@utils/lang'

const InnerFooter = styled(Footer)`
  padding: 24px 0;
`

const getFieldError = field => {
  if (!field.checkValidity()) {
    if (field.name === 'email' && field.value) {
      return tStr('errorFieldInvalid_s')
    }
    return tStr('errorFieldRequired_s')
  } else if (field.name === 'telephone' && !isValidPhone(field.value)) {
    return tStr('errorPhoneDigits_s')
  }
  return false
}
const getFieldErrors = evt => {
  const errors = {}
  let index = -1
  while (evt.currentTarget[++index]) {
    const target = evt.currentTarget[index]
    errors[target.name] = getFieldError(target)
  }
  return errors
}

const SignUpStep2 = ({
  error,
  errors,
  referral,
  phoneType,
  utm,
  userMarket,
  zipcode,
  qs,
  analyticsId,
  onSubmit,
  loading
}) => {
  const dispatch = useAppDispatch()
  const [clientSideError, setClientSideError] = useState(false)
  const [fieldErrors, setFieldErrors] = useState({})
  const [smsOptIn, setSMSOptIn] = useState(false)

  useMountEffect(() => {
    dispatch(clearApplicantError())
  })

  const validateField = evt => {
    setFieldErrors({ ...fieldErrors, [evt.target.name]: getFieldError(evt.target) })
  }

  const resetError = evt => {
    if (!getFieldError(evt.target)) {
      setFieldErrors({ ...fieldErrors, [evt.target.name]: false })
    }
  }

  const getErrorMsg = field => fieldErrors[field] || errors?.find(err => err.field === field)?.message

  const checkIncomplete = () => {
    const fields = ['first_name', 'last_name', 'telephone', 'email']
    let incomplete = false
    fields.forEach(field => {
      if (fieldErrors[field]) {
        incomplete = true
      }
    })
    return incomplete
  }

  const onFormSubmit = evt => {
    evt.preventDefault()
    if (loading) return
    setFieldErrors(getFieldErrors(evt))
    const formValid = evt.currentTarget.checkValidity()
    if (formValid) {
      submitSignupPixels()
      onSubmit(evt)
    }

    setClientSideError(!formValid)
  }
  const referred = referral && referral.status === 'success'
  const hasUtm = !!Object.keys(utm).length
  const referralParams = referral
    ? {
        medium: referral.code,
        source: utm.source ? utm.source : 'Runner Referral'
      }
    : {}

  return (
    <Step2Container>
      <div>
        <Form onSubmit={onFormSubmit} noValidate>
          <Section>
            <ErrorMessage>{error || (clientSideError && tStr('errorRequiredFieldsMissing_s'))}</ErrorMessage>
            <div className="title">{t('signUpTitle_s')}</div>
            <div className="subtitle">{t('signUpSubtitle_s', { market: userMarket.name })}</div>
            <Input type="hidden" name="phone_type" value={phoneType} />
            <Input type="hidden" name="query_string" value={qs} />
            <Input type="hidden" name="installation_id" value={analyticsId} />
            <Input type="hidden" name="zipcode" value={zipcode} />
            <Input type="hidden" name="market" value={userMarket.city} />
            <Input type="hidden" name="market_id" value={userMarket.id} />

            {hasUtm || referralParams ? (
              <Input
                type="hidden"
                name="utm_data"
                value={JSON.stringify({
                  ...utm,
                  ...referralParams // stuffing referral params here due to some backend needs
                })}
              />
            ) : null}
            <Fieldset label={tStr('signUpLegalName_s')}>
              <div className="nameFields">
                <div>
                  <Input
                    type="text"
                    placeholder={tStr('signUpFirstPlaceholder_s')}
                    label={tStr('signUpFirstLabel_s')}
                    name="first_name"
                    id="first_name"
                    hideLabel
                    errorMsg={getErrorMsg('first_name')}
                    required
                    onChange={resetError}
                    onBlur={validateField}
                  />
                </div>
                <div>
                  <Input
                    type="text"
                    placeholder={tStr('signUpLastPlaceholder_s')}
                    label={tStr('signUpLastLabel_s')}
                    name="last_name"
                    id="last_name"
                    hideLabel
                    errorMsg={getErrorMsg('last_name')}
                    required
                    onChange={resetError}
                    onBlur={validateField}
                  />
                </div>
              </div>
            </Fieldset>

            <Input
              label={tStr('signUpEmailLabel_s')}
              type="email"
              placeholder={tStr('signUpEmailPlaceholder_s')}
              name="email"
              id="email"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
              errorMsg={getErrorMsg('email')}
              required
              onChange={resetError}
              onBlur={validateField}
            />

            <Phone
              label={tStr('signUpPhoneLabel_s')}
              className="input"
              placeholder={tStr('signUpPhonePlaceholder_s')}
              autoComplete="off"
              name="telephone"
              id="telephone"
              errorMsg={getErrorMsg('telephone')}
              required
              onChange={resetError}
              onBlur={validateField}
            />

            <InputOption
              label={tStr('signUpSmsOptIn_s')}
              name="sms_opt_in"
              onClick={() => setSMSOptIn(!smsOptIn)}
              checked={smsOptIn}
              value={`${smsOptIn}`}
              className="sms"
              labelClass="smsLabel"
            />
            <div className="smsCaption">{t('signUpSmsCaption_s')}</div>

            <Input type="hidden" name="transportation" value="Car" />

            <ReferralInputs referred={referred} referral={referral} />

            <p id="tos">
              {t('signUpTos_s', {
                tos: (
                  <a target="_blank" href="https://favordelivery.com/tos" rel="noreferrer">
                    {t('signUpTosLink_s')}
                  </a>
                ),
                privacy: (
                  <a target="_blank" href="https://favordelivery.com/privacy" rel="noreferrer">
                    {t('signUpPrivacyLink_s')}
                  </a>
                )
              })}
            </p>

            <InnerFooter>
              <Button orange type="submit" disabled={checkIncomplete()} loading={loading} aria-describedby="tos">
                {t('signUpCta_s')}
              </Button>
            </InnerFooter>
          </Section>
        </Form>
      </div>
    </Step2Container>
  )
}

export default SignUpStep2
